import { memo, useEffect, useRef, useState } from "react";
import { useFrame, useLoader } from "@react-three/fiber";
import {
  CubeCamera,
  MeshTransmissionMaterial,
  useGLTF,
  useTexture,
} from "@react-three/drei";
import { RGBELoader } from "three-stdlib";
import { mapValue, random_choice } from "../common/utils";
import bumpImage from "../assets/bump.webp";
import art_studio from "../assets/art_studio_1k.hdr";
import crystalGltf from "../assets/crystal.gltf";
import { animated, easings, useSpring } from "@react-spring/three";
import Crystals from "./Crystals";
import { useTone } from "./Music/ToneProvider";
import { startSynth } from "./Music/Synth";
import { startBass, stopBass } from "./Music/Bass";
import { startPlayer, stopPlayer } from "./Music/Player";
import { startNoise, stopNoise } from "./Music/Noise";
import { startSampler, stopSampler } from "./Music/Sampler";
import { fxpreview } from "../fxhash";
import Cursor from "./Cursor";

const AnimatedTransmissionMaterial = animated(MeshTransmissionMaterial);

function CrystalBall({ isPlaying, setIsPlaying, features }) {
  const meter = useRef(null);
  const groupRef = useRef(null);
  const crystalRef = useRef(null);
  const [currentRot, setCurrentRot] = useState(0);
  const [hasPreview, setHasPreview] = useState(false);
  const [baseScale, setBaseScale] = useState(4);
  const [orientation, setOrientation] = useState("landscape");
  const [hovering, setHover] = useState(false);
  const [sphereColor, setSphereColor] = useState(null);
  const [musicLevel, setMusicLevel] = useState(0);

  const Tone = useTone();

  const { nodes } = useGLTF(crystalGltf);
  const texture = useLoader(RGBELoader, art_studio);
  const bumpMap = useTexture(bumpImage);

  useEffect(() => {
    console.log(window.$fxhashFeatures);
    const updateBaseScale = () => {
      setOrientation(
        window.innerHeight > window.innerWidth * 1.18 ? "portrait" : "landscape"
      );
      setBaseScale(orientation === "portrait" ? 3 : 4);
    };

    updateBaseScale();
    window.addEventListener("resize", updateBaseScale, { passive: true });
  }, [orientation]);

  useEffect(() => {
    if (!sphereColor) {
      setSphereColor(random_choice(features.palette.crystals));
    }
  }, [sphereColor, features.palette.crystals]);
  const springs = useSpring({
    config: {
      duration: 1000,
      easing: easings.easeInOutQuad,
    },
    distortion: hovering ? 20 : 8,
    color: isPlaying
      ? `hsl(${features.baseHue}, 60%, 60%)`
      : `hsl(${features.baseHue}, 0%, 100%)`,
  });

  const quickSprings = useSpring({
    config: {
      duration: 300,
      easing: easings.easeInOutQuad,
    },
    bumpScale: hovering ? 0.019 : 0.008,
    thickness: hovering ? features.thickness : features.thickness * 2,
    displacementScale: hovering ? 0.03 * (isPlaying ? 1.1 : 1) : 0.005,
  });

  useFrame((state, delta) => {
    groupRef.current.rotation.y = currentRot;
    const orbitSpeed = isPlaying ? features.speed / 90 : features.speed / 180;
    setCurrentRot(currentRot + delta * orbitSpeed);
    if (isPlaying) {
      setMusicLevel(mapValue(meter.current.getValue(), -50, -5, 0, 0.03));
    }
    if (!hasPreview && crystalRef.current.visible) {
      setHasPreview(true);
      window.setTimeout(() => {
        fxpreview();
      }, 5000);
    }
  });

  const handleClick = () => {
    if (isPlaying) {
      Tone.Transport.stop();
      stopBass();
      stopPlayer();
      stopSampler();
      stopNoise();
      setIsPlaying(false);
    } else {
      startBass();
      startSynth();
      startPlayer();
      startSampler();
      startNoise();

      meter.current = new Tone.Meter();
      Tone.Destination.connect(meter.current);

      Tone.Transport.start();
      setIsPlaying(true);
    }
  };

  return (
    <>
      <CubeCamera resolution={256} frames={1} envMap={texture}>
        {(texture) => (
          <>
            <Cursor isHovering={hovering} texture={texture} bumpMap={bumpMap} />
            <group scale={1 + Math.max(0, musicLevel)} ref={groupRef}>
              <mesh
                castShadow
                scale={baseScale}
                onPointerOver={() => setHover(true)}
                onPointerOut={() => setHover(false)}
                onClick={handleClick}
                ref={crystalRef}
              >
                <sphereGeometry args={[1, 64, 64]} />
                <AnimatedTransmissionMaterial
                  resolution={1024}
                  envMap={texture}
                  distortion={springs.distortion}
                  roughness={0.2}
                  bumpMap={bumpMap}
                  bumpScale={quickSprings.bumpScale}
                  displacementMap={bumpMap}
                  displacementScale={quickSprings.displacementScale}
                  thickness={quickSprings.thickness}
                  color={springs.color}
                />
              </mesh>
              <Crystals nodes={nodes} texture={texture} baseScale={baseScale} />
            </group>
          </>
        )}
      </CubeCamera>
    </>
  );
}

export default memo(CrystalBall);
